import { DealColumn, DealData } from "@/pages/deals/types";
import { toLocalDate } from "@/utils/datetime";

// toLocalDate hands the matched components straight to the Date constructor, which rolls
// impossible ones over: "2026-03-32" becomes April 1 and would be labelled Q2. Only a date
// that reads back exactly as written is a real close date.
function parseCloseDate(date: string | null): Date | null {
  if (!date) return null;

  const closeDate = toLocalDate(date);
  if (Number.isNaN(closeDate.getTime())) return null;

  // Anything not ISO-shaped went through the engine's own string parser, which accepts
  // formats whose impossible days roll over silently — "June 31, 2026" becomes July 1, moving
  // the deal from Q2 to Q3. expectedCloseAt is an ISO field, so treat a non-ISO value as no
  // date rather than guess at it.
  const isoParts = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoParts) return null;

  const [, year, month, day] = isoParts;
  const readsBack =
    closeDate.getFullYear() === Number(year) &&
    closeDate.getMonth() === Number(month) - 1 &&
    closeDate.getDate() === Number(day);

  return readsBack ? closeDate : null;
}

export function getQuarter(date: string | null): string {
  const closeDate = parseCloseDate(date);
  if (!closeDate) return "No Date";

  return `Q${Math.floor(closeDate.getMonth() / 3) + 1} ${closeDate.getFullYear()}`;
}

// Dead under manualSorting (DataTable drops getSortedRowModel), and it date-parses this
// column's label rather than a date, so it compares NaN — the API sorts the column by the
// expected close date.
export function quarterSortingFn(
  rowA: any,
  rowB: any,
  columnId: string
): number {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const dateA = new Date(a);
  const dateB = new Date(b);

  return dateA.getTime() - dateB.getTime();
}

export function addClosingQuarterGroup(data: DealData[]): DealData[] {
  return data.map((deal) => ({
    ...deal,
    [DealColumn.CLOSING_QUARTER]: getQuarter(deal.expectedCloseAt)
  }));
}
